import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { useMaintenanceObjectiveActions } from '@/common/hooks/queries/useMaintenanceObjectiveActions'
import { useMaintenanceObjectiveParts } from '@/common/hooks/queries/useMaintenanceObjectiveParts'
import { useMaintenanceObjectiveSystems } from '@/common/hooks/queries/useMaintenanceObjectiveSystems'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { MaintenanceObjectiveActionData } from '@/common/types/maintenance/MaintenanceObjectiveAction.type'
import { MaintenanceObjectivePartData } from '@/common/types/maintenance/MaintenanceObjectivePart.type'
import { MaintenanceObjectiveSystemData } from '@/common/types/maintenance/MaintenanceObjectiveSystem.type'
import {
  maintenanceSolicitationObjectiveSchema,
  MaintenanceSolicitationObjectiveValidationSchema,
  MaintenanceSolicitationValidationSchema,
} from '@/features/maintenanceSolicitations/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { PlusIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FieldArray, UseFieldArrayAppend, useForm } from 'react-hook-form'
import { Tooltip } from 'react-tooltip'

interface ModalAddMaintenanceSolicitationObjectiveProps {
  objectivesFields: FieldArray<MaintenanceSolicitationValidationSchema, 'objectives'>[]
  appendObjective: UseFieldArrayAppend<MaintenanceSolicitationValidationSchema, 'objectives'>
  equipmentId?: string
  disabled?: boolean
}

export const ModalAddMaintenanceSolicitationObjective = ({
  objectivesFields,
  appendObjective,
  equipmentId,
  disabled,
}: ModalAddMaintenanceSolicitationObjectiveProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchSystem, setSearchSystem] = useDebounce('')
  const [searchPart, setSearchPart] = useDebounce('')
  const [searchAction, setSearchAction] = useDebounce('')

  const formMethods = useForm<MaintenanceSolicitationObjectiveValidationSchema>({
    resolver: zodResolver(maintenanceSolicitationObjectiveSchema),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = formMethods

  const { data: objectiveSystemsOptions } = useMaintenanceObjectiveSystems({
    filters: {
      search: searchSystem,
      equipmentId,
    },
    options: {
      enabled: !!equipmentId,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveSystemData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const { data: objectivePartsOptions } = useMaintenanceObjectiveParts({
    filters: {
      search: searchPart,
      objectiveSystem: watch('system_id')?.value,
      equipmentId,
    },
    options: {
      enabled: !!watch('system_id')?.value && !!equipmentId,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectivePartData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const { data: objectiveActionsOptions } = useMaintenanceObjectiveActions({
    filters: {
      search: searchAction,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveActionData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const onSubmit = (data: MaintenanceSolicitationObjectiveValidationSchema) => {
    if (
      objectivesFields.some(
        (field) =>
          field.system_id.value === data.system_id?.value &&
          field.part_id.value === data.part_id?.value &&
          field.action_id.value === data.action_id?.value,
      )
    ) {
      setMessages(['Esse objeto já foi adicionado'])
      return
    }

    appendObjective({
      system_id: data.system_id,
      part_id: data.part_id,
      action_id: data.action_id,
    })

    setIsOpen(false)
    setMessages(false)
  }

  useEffect(() => {
    reset()
  }, [isOpen])

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={
          <Button
            type="button"
            variant="neutral"
            className="flex w-full flex-col items-center gap-y-2 bg-white-100 hover:bg-white-200 border border-dashed border-white-200 text-blue-800 p-4 rounded-lg"
            disabled={!equipmentId || disabled}
            data-tooltip-id="add-objective-tooltip"
          >
            <PlusIcon strokeWidth={1} />

            <p>Adicionar Objeto</p>
          </Button>
        }
        triggerAsChild
        title="Adicionar Objeto de manutenção"
        useCancellableFooter
        confirmButtonText="Adicionar Objeto"
        confirmButtonClassName="bg-primary-blue text-white-800"
        onConfirm={handleSubmit(onSubmit)}
        body={
          <div className="flex flex-col gap-y-4">
            <SelectController
              control={control}
              name="system_id"
              label="Sistema"
              markAsRequired
              options={objectiveSystemsOptions || []}
              onInputChange={setSearchSystem}
              errorMessage={errors.system_id?.message}
            />

            <div data-tooltip-id="part-tooltip">
              <SelectController
                control={control}
                name="part_id"
                label="Parte"
                markAsRequired
                options={objectivePartsOptions || []}
                onInputChange={setSearchPart}
                errorMessage={errors.part_id?.message}
                isDisabled={!watch('system_id')?.value}
              />

              {!watch('system_id')?.value && (
                <Tooltip id="part-tooltip" className="text-sm">
                  Selecione um sistema para habilitar a seleção de parte de manutenção
                </Tooltip>
              )}
            </div>

            <SelectController
              control={control}
              name="action_id"
              label="Ação"
              markAsRequired
              options={objectiveActionsOptions || []}
              onInputChange={setSearchAction}
              errorMessage={errors.action_id?.message}
            />
          </div>
        }
      />

      {!equipmentId && (
        <Tooltip id="add-objective-tooltip" className="text-sm">
          Selecione um equipamento para adicionar objetos de manutenção
        </Tooltip>
      )}

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </>
  )
}
